import type { KeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';

const DEFAULT_RESET_MS = 750;

export interface UseLetterTypeaheadOptions<TItem> {
  /**
   * Ordered list of items; position determines tie-break order.
   */
  items: TItem[];
  /**
   * Extract the searchable text from an item (case-insensitive `startsWith`).
   */
  getText: (item: TItem) => string;
  /**
   * Called with the matched item, or not at all when nothing matches.
   */
  onMatch: (item: TItem) => void;
  /**
   * Window (ms) within which repeated same-letter presses cycle ties.
   *
   * @default 750
   */
  resetMs?: number;
}

/**
 * Returns an `onKeyDown` handler implementing native `<select>`-style letter
 * typeahead with cycling on repeated same-letter presses.
 */
export const useLetterTypeahead = <TItem>({
  items,
  getText,
  onMatch,
  resetMs = DEFAULT_RESET_MS,
}: UseLetterTypeaheadOptions<TItem>) => {
  const stateRef = useRef({ index: -1, letter: '', timestamp: 0 });

  // Refs keep the returned handler stable across inline callbacks.
  const getTextRef = useRef(getText);
  getTextRef.current = getText;
  const onMatchRef = useRef(onMatch);
  onMatchRef.current = onMatch;

  return useCallback(
    (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const { key } = event;
      if (key.length !== 1 || !/[a-z]/i.test(key)) return;

      const letter = key.toLowerCase();
      const matches = items
        .map((item, index) => ({ index, item }))
        .filter(({ item }) =>
          getTextRef.current(item).toLowerCase().startsWith(letter),
        );
      if (matches.length === 0) return;

      const now = Date.now();
      const {
        index: lastIndex,
        letter: lastLetter,
        timestamp: lastTimestamp,
      } = stateRef.current;
      const isSameLetter =
        lastLetter === letter && now - lastTimestamp < resetMs;
      const currentPosition = isSameLetter
        ? matches.findIndex(match => match.index === lastIndex)
        : -1;
      const next = matches[(currentPosition + 1) % matches.length];

      stateRef.current = { index: next.index, letter, timestamp: now };
      onMatchRef.current(next.item);
      event.preventDefault();
    },
    [items, resetMs],
  );
};
